import { z } from "zod";

const positionSchema = z.enum(["bottom-right", "bottom-left", "top-right", "top-left"]);

const themeSchema = z.enum(["light", "dark", "system"]);

const widgetEmbedSchema = z.object({
  tenantId: z.string().min(1, "tenantId is required"),
  siteId: z.string().min(1, "siteId is required"),
  position: positionSchema,
  theme: themeSchema,
  chatWindowTitle: z.string().min(1),
  launcherButtonText: z.string().min(1),
  launcherFillColor: z.string().min(1),
  userBubbleColor: z.string().min(1),
  replyBubbleColor: z.string().min(1),
  surfaceColor: z.string().min(1),
  userTextColor: z.string().min(1).optional(),
  replyTextColor: z.string().min(1).optional(),
  launcherTextColor: z.string().min(1).optional(),
  headerGradient: z.boolean().optional(),
  showAvatars: z.boolean().optional(),
  cornerRadius: z.number().int().min(0).max(32).optional(),
  launcherRadius: z.number().int().min(0).max(48).optional(),
  bubbleShape: z.enum(["rounded", "pill"]).optional(),
  /** Optional override for API host; otherwise derived from the script URL at bootstrap. */
  apiOrigin: z.string().min(1).optional(),
  /** Optional Fish-style TTS proxy URL (POST JSON: text, voice_id, model). */
  ttsEndpoint: z.string().min(1).optional(),
  /**
   * Optional override for the ElevenLabs Scribe v1 server STT proxy URL
   * (`/api/engage/speech/stt`). Used when an operator points a legacy embed at
   * a third-party transcription endpoint. When unset (the common case) the
   * widget defaults to `${apiOrigin}/api/engage/speech/stt`.
   */
  sttEndpoint: z.string().min(1).optional(),
  // `anonKey` removed 2026-05-25 (Andrew confirmed no TSAI customers shipped
  // it). Legacy embeds that pass `data-anon-key` will have the attribute
  // silently ignored; TTS/STT now authenticate server-side via FISH_API_KEY +
  // ELEVENLABS_API_KEY on the `/api/engage/speech` + `/api/engage/speech/stt`
  // routes respectively.
  fishVoiceId: z.string().min(1).optional(),
  fishModel: z.string().min(1).optional(),
  /**
   * Optional DOM element id for a target-mounted launcher. When set and the
   * element exists on the host page, the widget loader mounts the
   * `<skinny-widget>` host inside that element instead of `document.body`.
   * The exact behavior depends on `targetMode` below. If the element is
   * missing at load time the loader logs a warn and falls back to the
   * floating launcher on `document.body`. Stored as a bare id; the leading
   * `#` is stripped upstream by `normalizeTargetId`.
   */
  targetId: z.string().min(1).optional(),
  /**
   * How a resolved `targetId` is rendered:
   *  - `"button"` — Advanced placement (TSAI parity): the launcher BUTTON is
   *    mounted in normal flow inside the target element while the chat panel
   *    slides over from a body-level overlay. Emitted via `data-target`.
   *  - `"panel"` — In-place panel (#117): the chat panel renders inline inside
   *    the target element with no floating FAB. Emitted via `data-target-id`.
   * Ignored when `targetId` is unset (floating launcher).
   */
  targetMode: z.enum(["button", "panel"]).default("panel"),
  /**
   * Share-link sandbox JWT (`/test?code=…` only). When set, chat requests carry
   * `test_token` in the JSON body instead of relying on anonymous tenant/site ids.
   */
  testToken: z.string().min(1).optional(),
  /**
   * Signed-in portal operator widget. Sends the session cookie and asks the
   * chat route to ground answers in Customer Help. Public and customer-site
   * embeds leave this unset.
   */
  portalSession: z.boolean().optional(),
});

const responsivePositionSchema = positionSchema.optional();

export const widgetConfigSchema = widgetEmbedSchema.extend({
  apiOrigin: z.string().min(1),
  positionDesktop: responsivePositionSchema,
  positionMobile: responsivePositionSchema,
  positionTablet: responsivePositionSchema,
});

export type WidgetEmbedConfig = z.infer<typeof widgetEmbedSchema>;
export type WidgetConfig = z.infer<typeof widgetConfigSchema>;

/** Dataset parse result including optional breakpoint position overrides. */
export type WidgetEmbedParseResult = WidgetEmbedConfig &
  ResponsivePositionOverrides & {
    readonly position: WidgetPosition;
  };

export type WidgetPosition = z.infer<typeof positionSchema>;

export type WidgetThemeMode = z.infer<typeof themeSchema>;

const POSITION_ALIASES: Record<string, WidgetPosition> = {
  "bottom-right": "bottom-right",
  bottomright: "bottom-right",
  br: "bottom-right",
  "bottom-left": "bottom-left",
  bottomleft: "bottom-left",
  bl: "bottom-left",
  "top-right": "top-right",
  topright: "top-right",
  tr: "top-right",
  "top-left": "top-left",
  topleft: "top-left",
  tl: "top-left",
};

/**
 * Reads the first non-empty dataset value for any of the given keys (camelCase, lowercase,
 * or snake_case) so legacy `data-*` spellings keep working across browsers.
 */
function readFirstDatasetString(dataset: DOMStringMap, keys: readonly string[]): string | undefined {
  const record = dataset as Record<string, string | undefined>;
  for (const key of keys) {
    const value = record[key];
    if (typeof value === "string" && value.trim().length > 0) {
      return value.trim();
    }
  }
  return undefined;
}

function normalizePosition(raw: string | undefined): WidgetPosition {
  if (!raw) {
    return "bottom-right";
  }
  const key = raw.trim().toLowerCase().replace(/\s+/g, "-");
  return POSITION_ALIASES[key] ?? "bottom-right";
}

function readResponsivePosition(
  dataset: DOMStringMap,
  keys: readonly string[],
): WidgetPosition | undefined {
  const raw = readFirstDatasetString(dataset, keys);
  return raw ? normalizePosition(raw) : undefined;
}

export type ResponsivePositionOverrides = {
  readonly positionDesktop?: WidgetPosition;
  readonly positionMobile?: WidgetPosition;
  readonly positionTablet?: WidgetPosition;
};

/**
 * Legacy loader + embed breakpoints: mobile ≤575.98px, tablet 576–1199.98px, else desktop.
 */
export function resolveResponsiveWidgetPosition(
  basePosition: WidgetPosition,
  overrides?: ResponsivePositionOverrides,
): WidgetPosition {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return basePosition;
  }
  try {
    const mobile = window.matchMedia("(max-width: 575.98px)").matches;
    const tablet = window.matchMedia("(min-width: 576px) and (max-width: 1199.98px)").matches;
    if (mobile && overrides?.positionMobile) {
      return overrides.positionMobile;
    }
    if (tablet && overrides?.positionTablet) {
      return overrides.positionTablet;
    }
    if (!mobile && !tablet && overrides?.positionDesktop) {
      return overrides.positionDesktop;
    }
  } catch {
    /* ignore */
  }
  return basePosition;
}

function normalizeTheme(raw: string | undefined): WidgetThemeMode {
  if (raw === "light" || raw === "dark" || raw === "system") {
    return raw;
  }
  return "system";
}

const DEFAULT_SHOWROOM = {
  chatWindowTitle: "Chat",
  launcherButtonText: "Chat",
  launcherFillColor: "#15803d",
  userBubbleColor: "#ecfdf5",
  replyBubbleColor: "#f4f4f5",
  surfaceColor: "#ffffff",
} as const;

/**
 * Parses legacy embed attributes from the executing `<script>` tag (`data-*` / `dataset`).
 * Callers must merge `apiOrigin` (typically `new URL(script.src).origin`) before persisting `WidgetConfig`.
 */
export function parseWidgetConfigFromDataset(script: HTMLScriptElement): WidgetEmbedParseResult {
  const { dataset } = script;

  const tenantId = readFirstDatasetString(dataset, ["tenantId", "tenantid", "tenant_id"]);
  const siteId = readFirstDatasetString(dataset, ["siteId", "siteid", "site_id"]);

  const position = normalizePosition(
    readFirstDatasetString(dataset, ["position", "pos", "widgetposition", "widget_position"]),
  );
  const positionDesktop = readResponsivePosition(dataset, [
    "positionDesktop",
    "positiondesktop",
    "position_desktop",
  ]);
  const positionMobile = readResponsivePosition(dataset, [
    "positionMobile",
    "positionmobile",
    "position_mobile",
  ]);
  const positionTablet = readResponsivePosition(dataset, [
    "positionTablet",
    "positiontablet",
    "position_tablet",
  ]);
  const theme = normalizeTheme(readFirstDatasetString(dataset, ["theme", "thememode", "theme_mode"]));

  // Target placement split:
  //  - `data-target-id` (panel) → in-place inline panel (#117).
  //  - `data-target` / `data-mount-target` (button) → Advanced anchored
  //    launcher button (TSAI parity). When both spellings are present, the
  //    explicit panel id (`data-target-id`) wins.
  const panelTargetId = readFirstDatasetString(dataset, ["targetId", "targetid", "target_id"]);
  const buttonTargetId = readFirstDatasetString(dataset, [
    "target",
    "mountTarget",
    "mounttarget",
    "mount_target",
  ]);
  const targetId = panelTargetId ?? buttonTargetId;
  const targetMode: "button" | "panel" = panelTargetId ? "panel" : buttonTargetId ? "button" : "panel";

  const parsed = widgetEmbedSchema.safeParse({
    tenantId: tenantId ?? "",
    siteId: siteId ?? "",
    position,
    theme,
    chatWindowTitle:
      readFirstDatasetString(dataset, [
        "chatWindowTitle",
        "chatwindowtitle",
        "chat_window_title",
        "name",
        "widgetname",
        "widget_name",
        "title",
      ]) ?? DEFAULT_SHOWROOM.chatWindowTitle,
    launcherButtonText:
      readFirstDatasetString(dataset, [
        "launcherButtonText",
        "launcherbuttontext",
        "launcher_button_text",
        "buttonText",
        "buttontext",
        "button_text",
      ]) ?? DEFAULT_SHOWROOM.launcherButtonText,
    launcherFillColor:
      readFirstDatasetString(dataset, [
        "launcherFillColor",
        "launcherfillcolor",
        "launcher_fill_color",
        "accentColor",
        "accentcolor",
        "accent_color",
      ]) ?? DEFAULT_SHOWROOM.launcherFillColor,
    userBubbleColor:
      readFirstDatasetString(dataset, [
        "userBubbleColor",
        "userbubblecolor",
        "user_bubble_color",
        "userColor",
        "usercolor",
        "user_color",
      ]) ?? DEFAULT_SHOWROOM.userBubbleColor,
    replyBubbleColor:
      readFirstDatasetString(dataset, [
        "replyBubbleColor",
        "replybubblecolor",
        "reply_bubble_color",
        "assistantColor",
        "assistantcolor",
        "assistant_color",
      ]) ?? DEFAULT_SHOWROOM.replyBubbleColor,
    surfaceColor:
      readFirstDatasetString(dataset, [
        "surfaceColor",
        "surfacecolor",
        "surface_color",
        "backgroundColor",
        "backgroundcolor",
        "background_color",
      ]) ?? DEFAULT_SHOWROOM.surfaceColor,
    apiOrigin: readFirstDatasetString(dataset, ["apiOrigin", "apiorigin", "api_origin", "apiBase", "apibase", "api_base"]),
    ttsEndpoint: readFirstDatasetString(dataset, [
      "ttsEndpoint",
      "ttsendpoint",
      "tts_endpoint",
      "speechTtsUrl",
      "speechttsurl",
      "speech_tts_url",
      "fishTtsUrl",
      "fishttsurl",
      "fish_tts_url",
    ]),
    sttEndpoint: readFirstDatasetString(dataset, [
      "sttEndpoint",
      "sttendpoint",
      "stt_endpoint",
      "speechSttUrl",
      "speechstturl",
      "speech_stt_url",
      "elevenlabsSttUrl",
      "elevenlabsstturl",
      "elevenlabs_stt_url",
    ]),
    // `anonKey` (data-anon-key / data-supabase-anon-key / data-public-anon-key)
    // is no longer parsed — see schema comment above. Legacy embeds with
    // these attributes will have them silently ignored.
    fishVoiceId: readFirstDatasetString(dataset, [
      "fishVoiceId",
      "fishvoiceid",
      "fish_voice_id",
      "voiceId",
      "voiceid",
      "voice_id",
    ]),
    fishModel: readFirstDatasetString(dataset, [
      "fishModel",
      "fishmodel",
      "fish_model",
      "ttsModel",
      "ttsmodel",
      "tts_model",
    ]),
    targetId,
    targetMode,
    testToken: readFirstDatasetString(dataset, ["testToken", "testtoken", "test_token"]),
    portalSession:
      readFirstDatasetString(dataset, ["portalSession", "portal_session"]) === "true",
  });

  if (!parsed.success) {
    throw new Error(
      `[Scale2Rev Engage Widget] Invalid configuration: ${parsed.error.issues
        .map((i) => `${i.path.join(".")}: ${i.message}`)
        .join("; ")}`,
    );
  }

  const embed = parsed.data;
  const responsiveOverrides: ResponsivePositionOverrides = {
    positionDesktop,
    positionMobile,
    positionTablet,
  };

  return {
    ...embed,
    position: resolveResponsiveWidgetPosition(position, responsiveOverrides),
    positionDesktop,
    positionMobile,
    positionTablet,
  };
}

/** Merge responsive overrides into a persisted `WidgetConfig` JSON blob. */
export function applyResponsivePositionToConfig(config: WidgetConfig): WidgetConfig {
  return {
    ...config,
    position: resolveResponsiveWidgetPosition(config.position, {
      positionDesktop: config.positionDesktop,
      positionMobile: config.positionMobile,
      positionTablet: config.positionTablet,
    }),
  };
}
